import React, { useState, useEffect } from "react";
import { FaTimes, FaDownload, FaTrash, FaSpinner, FaUpload } from "react-icons/fa";
import { useUsers } from "../context/UsersContext";
import {
  descargarBackupExcel,
  descargarPlantillaExcel,
  crearEstudiantesMasivo,
  crearProfesoresMasivo,
  eliminarEstudiantesMasivo,
  eliminarProfesoresMasivo,
} from "../services/gestionMasivaService";
import Notification from "./Notification";

const GestionMasivaModal = ({ isOpen, onClose, tipo, onResultadoMasivo }) => {
  const { students, teachers, salones, fetchSalones, fetchStudents, fetchTeachers } = useUsers();
  const [accion, setAccion] = useState("backup"); // 'backup' o 'eliminar'
  const [tipoEliminacion, setTipoEliminacion] = useState("todos");
  const [salonSeleccionado, setSalonSeleccionado] = useState("");
  const [usuariosSeleccionados, setUsuariosSeleccionados] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [resultadoCreacion, setResultadoCreacion] = useState(null);
  const [notification, setNotification] = useState(null);

  const usuarios = tipo === "students" ? students : teachers;

  useEffect(() => {
    if (isOpen && salones.length === 0) {
      fetchSalones();
    }
  }, [isOpen, salones, fetchSalones]);

  const usuariosFiltrados = usuarios.filter((u) => {
    const nombreCompleto =
      `${u.first_name} ${u.second_name || ""} ${u.first_surname} ${u.second_surname || ""}`.toLowerCase();
    const matchSearch = nombreCompleto.includes(searchTerm.toLowerCase());

    if (tipoEliminacion === "salon" && tipo === "students") {
      // 🔹 CORRECCIÓN: Buscar por salon_id en estudiantes
      const salonMatch = salones.find(s => s.nombre === u.salon_nombre);
      return matchSearch && salonMatch && salonMatch.id === parseInt(salonSeleccionado);
    }
    return matchSearch;
  });

  const handleDescargarBackup = async () => {
    setLoading(true);
    try {
      const response = await descargarBackupExcel(tipo);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `backup_${tipo}_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      setNotification({
        type: "success",
        message: "Backup descargado exitosamente",
      });
    } catch (error) {
      setNotification({
        type: "error",
        message: "Error al descargar backup",
      });
    } finally {
      setLoading(false);
    }
  };

  // Descargar plantilla vacía
  const handleDescargarPlantilla = async () => {
    setLoading(true);
    try {
      const response = await descargarPlantillaExcel(tipo);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `plantilla_${tipo}_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      setNotification({
        type: "success",
        message: "Plantilla descargada exitosamente",
      });
    } catch (error) {
      setNotification({
        type: "error",
        message: "Error al descargar plantilla",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async () => {
    setConfirmModalOpen(false);
    setLoading(true);

    try {
      const data = {
        tipo: tipoEliminacion,
        ...(tipoEliminacion === "salon" && { salon_id: parseInt(salonSeleccionado) }),
        ...(tipoEliminacion === "seleccion" && {
          [tipo === "students" ? "estudiantes_ids" : "profesores_ids"]:
            usuariosSeleccionados,
        }),
      };

      if (tipo === "students") {
        await eliminarEstudiantesMasivo(data);
        await fetchStudents(true);
      } else {
        await eliminarProfesoresMasivo(data);
        await fetchTeachers(true);
      }

      setNotification({
        type: "success",
        message: `${tipo} eliminados exitosamente`,
      });
      setUsuariosSeleccionados([]);
      setSalonSeleccionado("");
      onClose();
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Error al eliminar",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSeleccion = (userId) => {
    setUsuariosSeleccionados((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const seleccionarTodos = () => {
    if (usuariosSeleccionados.length === usuariosFiltrados.length) {
      setUsuariosSeleccionados([]);
    } else {
      setUsuariosSeleccionados(usuariosFiltrados.map((u) => u.user_id));
    }
  };

  // Manejar selección de archivo
  const handleArchivoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea un archivo Excel
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (!validTypes.includes(file.type)) {
        setNotification({
          type: "error",
          message: "Solo se permiten archivos Excel (.xlsx, .xls)",
        });
        return;
      }
      setArchivoSeleccionado(file);
      setResultadoCreacion(null);
    }
  };

  // Crear usuarios masivamente
  const handleCrearMasivo = async () => {
    if (!archivoSeleccionado) {
      setNotification({
        type: "error",
        message: "Debe seleccionar un archivo Excel",
      });
      return;
    }

    setLoading(true);

    try {
      const response =
        tipo === "students"
          ? await crearEstudiantesMasivo(archivoSeleccionado)
          : await crearProfesoresMasivo(archivoSeleccionado);

      // 🔹 SIEMPRE ESTABLECER EL RESULTADO PROGRESIVO
      if (response && response.data && onResultadoMasivo) {
        onResultadoMasivo(response.data);
      }

      // Refrescar datos
      if (tipo === "students") {
        await fetchStudents(true);
      } else {
        await fetchTeachers(true);
      }

      setArchivoSeleccionado(null);
      
      // Limpiar el input de archivo
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';

      // Cerrar el modal después de procesar
      onClose();

    } catch (error) {
      
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Error al crear usuarios",
      });

      // También enviar errores al padre
      if (error.response?.data && onResultadoMasivo) {
        onResultadoMasivo({
          estudiantesCreados: 0,
          profesoresCreados: 0,
          errores: error.response.data.errores || ["Error desconocido"]
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-main-bg-sidebar text-white p-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">
              Gestión Masiva de {tipo === "students" ? "Estudiantes" : "Profesores"}
            </h2>
            <button onClick={onClose} className="text-white hover:text-gray-300">
              <FaTimes size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            {/* Selector de acción */}
            <div className="mb-6">
              <label className="block text-gray-700 font-bold mb-2">Acción:</label>
              <div className="flex gap-4">
                <button
                  onClick={() => setAccion("backup")}
                  className={`flex-1 p-3 rounded-lg border-2 ${
                    accion === "backup"
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300"
                  }`}
                >
                  <FaDownload className="inline mr-2" />
                  Descargar Backup
                </button>
                {/* 🔹 NUEVO: Botón Crear */}
                <button
                  onClick={() => {
                    setAccion("crear");
                    setArchivoSeleccionado(null);
                    setResultadoCreacion(null);
                  }}
                  className={`flex-1 p-3 rounded-lg border-2 ${
                    accion === "crear"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-300"
                  }`}
                >
                  <FaUpload className="inline mr-2" />
                  Crear Masivamente
                </button>
                {/*  */}
                <button
                  onClick={() => setAccion("eliminar")}
                  className={`flex-1 p-3 rounded-lg border-2 ${
                    accion === "eliminar"
                      ? "border-red-500 bg-red-50"
                      : "border-gray-300"
                  }`}
                >
                  <FaTrash className="inline mr-2" />
                  Eliminar
                </button>
              </div>
            </div>

            {accion === "backup" && (
              <div className="text-center">
                <p className="mb-4 text-gray-600">
                  Se generará un archivo Excel con todos los {tipo} organizados por salón
                </p>
                <button
                  onClick={handleDescargarBackup}
                  disabled={loading}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center mx-auto"
                >
                  {loading ? (
                    <FaSpinner className="animate-spin mr-2" />
                  ) : (
                    <FaDownload className="mr-2" />
                  )}
                  Descargar Backup
                </button>
              </div>
            )}

            {/* 🔹 NUEVO: Sección Crear */}
            {accion === "crear" && (
              <div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="font-bold text-blue-900 mb-2">
                    📋 Instrucciones:
                  </h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                    <li>Descargue primero el backup para tener la estructura correcta</li>
                    <li>Llene el archivo Excel con los nuevos {tipo}</li>
                    <li>
                      <strong>Elimine la columna ID</strong> y la hoja RESUMEN si existe
                    </li>
                    <li>Asegúrese de que todos los campos requeridos estén completos</li>
                    <li>Suba el archivo modificado</li>
                  </ol>
                </div>

                {/* 🔹 NUEVO: Botón para descargar plantilla */}
                <div className="mb-6 text-center">
                  <button
                    onClick={handleDescargarPlantilla}
                    disabled={loading}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center mx-auto mb-2"
                  >
                    {loading ? (
                      <FaSpinner className="animate-spin mr-2" />
                    ) : (
                      <FaDownload className="mr-2" />
                    )}
                    Descargar Plantilla Excel
                  </button>
                  <p className="text-xs text-gray-500">
                    Descargue esta plantilla para tener la estructura correcta
                  </p>
                </div>

                {/* Input de archivo */}
                <div className="mb-4">
                  <label className="block text-gray-700 font-bold mb-2">
                    Seleccionar Archivo Excel:
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleArchivoChange}
                    className="w-full p-2 border rounded"
                  />
                  {archivoSeleccionado && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ Archivo seleccionado: {archivoSeleccionado.name}
                    </p>
                  )}
                </div>

                {/* Botón crear */}
                <button
                  onClick={handleCrearMasivo}
                  disabled={loading || !archivoSeleccionado}
                  className="w-full bg-green hover:bg-green-600 text-white font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <FaSpinner className="animate-spin mr-2" />
                  ) : (
                    <FaUpload className="mr-2" />
                  )}
                  Crear {tipo} desde Excel
                </button>

                {/* Resultados */}
                {resultadoCreacion && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-bold mb-2">Resultados:</h3>
                    <p className="text-green-600">
                      ✓ Creados exitosamente: {resultadoCreacion.estudiantesCreados || resultadoCreacion.profesoresCreados}
                    </p>
                    {resultadoCreacion.errores && (
                      <div className="mt-2">
                        <p className="text-red-600 font-bold">❌ Errores:</p>
                        <ul className="list-disc list-inside text-sm text-gray-700 max-h-40 overflow-y-auto">
                          {resultadoCreacion.errores.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {accion === "eliminar" && (
              <>
                {/* Tipo de eliminación */}
                <div className="mb-4">
                  <label className="block text-gray-700 font-bold mb-2">
                    Tipo de Eliminación:
                  </label>
                  <select
                    value={tipoEliminacion}
                    onChange={(e) => {
                      setTipoEliminacion(e.target.value);
                      setUsuariosSeleccionados([]);
                    }}
                    className="w-full p-2 border rounded"
                  >
                    <option value="todos">Eliminar Todos</option>
                    {tipo === "students" && (
                      <option value="salon">Por Salón</option>
                    )}
                    <option value="seleccion">Selección Específica</option>
                  </select>
                </div>

                {/* Filtro por salón */}
                {tipoEliminacion === "salon" && tipo === "students" && (
                    <div className="mb-4">
                    <label className="block text-gray-700 font-bold mb-2">
                        Seleccionar Salón:
                    </label>
                    <select
                        value={salonSeleccionado}
                        onChange={(e) => {
                        setSalonSeleccionado(e.target.value);
                        setUsuariosSeleccionados([]); // Resetear selección
                        }}
                        className="w-full p-2 border rounded"
                    >
                        <option value="">Seleccione un salón</option>
                        {salones.map((salon) => (
                        <option key={salon.id} value={salon.id}>
                            {salon.nombre} - {salon.grado}
                        </option>
                        ))}
                    </select>
                    {salonSeleccionado && (
                        <p className="text-sm text-gray-600 mt-2">
                        {usuariosFiltrados.length} estudiante(s) en este salón
                        </p>
                    )}
                    </div>
                )}

                {/* Selección específica */}
                {tipoEliminacion === "seleccion" && (
                    <>
                    <div className="mb-4">
                        <input
                        type="text"
                        placeholder="Buscar por nombre..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 border rounded"
                        />
                    </div>

                    <div className="mb-2 flex justify-between items-center">
                        <button
                        onClick={seleccionarTodos}
                        className="text-blue-500 hover:underline"
                        >
                        {usuariosSeleccionados.length === usuariosFiltrados.length
                            ? "Deseleccionar Todos"
                            : "Seleccionar Todos"}
                        </button>
                        <span className="text-gray-600">
                        {usuariosSeleccionados.length} seleccionado(s)
                        </span>
                    </div>

                    <div className="max-h-64 overflow-y-auto border rounded p-2">
                        {usuariosFiltrados.length === 0 ? (
                        <p className="text-center text-gray-500 py-4">
                            No se encontraron {tipo}
                        </p>
                        ) : (
                        usuariosFiltrados.map((usuario) => (
                            <label
                            key={usuario.user_id}
                            className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                            >
                            <input
                                type="checkbox"
                                checked={usuariosSeleccionados.includes(usuario.user_id)}
                                onChange={() => toggleSeleccion(usuario.user_id)}
                                className="mr-3"
                            />
                            <span>
                                {usuario.first_name} {usuario.second_name || ""}{" "}
                                {usuario.first_surname} {usuario.second_surname || ""}
                                {tipo === "students" && (
                                <span className="text-gray-500 text-sm ml-2">
                                    ({usuario.salon_nombre || "Sin salón"} - {usuario.salon_grado || ""})
                                </span>
                                )}
                            </span>
                            </label>
                        ))
                        )}
                    </div>
                    </>
                )}

                {/* Botón eliminar */}
                <div className="mt-6">
                    <button
                    onClick={() => setConfirmModalOpen(true)}
                    disabled={
                        loading ||
                        (tipoEliminacion === "seleccion" &&
                        usuariosSeleccionados.length === 0) ||
                        (tipoEliminacion === "salon" && !salonSeleccionado)
                    }
                    className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                    {loading ? (
                        <FaSpinner className="animate-spin inline mr-2" />
                    ) : (
                        <FaTrash className="inline mr-2" />
                    )}
                    Eliminar {tipo}
                    </button>
                </div>
                </>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmación */}
      {confirmModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h3 className="text-xl font-bold mb-4 text-red-600">
              ⚠️ Confirmar Eliminación
            </h3>
            <p className="mb-6">
              ¿Está seguro que desea eliminar{" "}
              {tipoEliminacion === "todos"
                ? `TODOS los ${tipo}`
                : tipoEliminacion === "salon"
                ? `los ${tipo} del salón seleccionado`
                : `${usuariosSeleccionados.length} ${tipo} seleccionado(s)`}
              ?
              <br />
              <strong className="text-red-600">Esta acción no se puede deshacer.</strong>
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setConfirmModalOpen(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 rounded"
              >
                Cancelar
              </button>
              <button
                onClick={handleEliminar}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-2 rounded"
              >
                Confirmar Eliminación
              </button>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </>
  );
};

export default GestionMasivaModal;